import React from 'react';
import { Link } from 'react-router-dom';
import { Row, Col } from 'antd';
import { isNull } from 'lodash';

import {
  ContactPersonModel,
  CustomerInfoResponseModel,
  CustomerMyBasePayTeamModel,
} from 'api/backofficeApiClient';
import BasePageCard from 'components/wrappers/BasePageCard';
import PrimaryContactInfoSection from 'components/PrimaryContactInfoSection';
import { getWebsiteName, getWebsiteLink } from 'utils/urlUtils';
import {
  getClientSizeLabel,
  getAbilityToPayLabel,
  getYearsInBusinessLabel,
  getProjectEstimation,
} from 'utils/clientUtils';
import { getLocation } from 'utils/locationUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { getFullName } from 'utils/personUtils';
import { formatNumberAsCurrency } from 'utils/numberUtils';

type Props = {
  clientDetails: CustomerInfoResponseModel;
};

const getMyBasePayTeamDisplayValue = (
  team: CustomerMyBasePayTeamModel | undefined,
  nameKey: keyof Pick<
    CustomerMyBasePayTeamModel,
    | 'mbpClientSuccessPartner'
    | 'mbpBillingRepresentative'
    | 'mspSalesRepresentative'
    | 'mbpCollectionsRepresentative'
    | 'mspWorkforceSpecialist'
  >,
) => {
  const value = team?.[nameKey];
  return typeof value === 'string' ? value.trim() : '';
};

export default function ClientMainInfoSection(props: Props) {
  const isAdminRole = useAppSelector(isAdminSelector);
  const { clientDetails } = props;

  const {
    customerId,
    customerKey,
    customerInfo,
    primaryContacts,
    billingInfo,
    additionalInfo,
    recruiterCompanyName,
    recruiterCompanyKey,
    customerGroupName,
    myBasePayTeam,
  } = clientDetails;
  const myBasePayTeamResolved =
    myBasePayTeam ??
    (clientDetails as { MyBasePayTeam?: CustomerMyBasePayTeamModel })
      ?.MyBasePayTeam;
  const additionalInfoResolved =
    additionalInfo ?? (clientDetails as { AdditionalInfo?: typeof additionalInfo })
      ?.AdditionalInfo;
  const {
    countryName,
    address,
    city,
    internationalCityName,
    state,
    internationalStateName,
    zip,
  } = customerInfo.location;
  const mainAddress = getLocation(
    countryName,
    address,
    state || internationalStateName,
    city || internationalCityName,
    zip,
  );
  const billingContacts = billingInfo.billingContacts || [];
  const isContactFilled = (contact: ContactPersonModel) =>
    !!contact.email ||
    !!contact.firstName ||
    !!contact.lastName ||
    !!contact.title ||
    !!contact.phoneNumber;

  const isPrimaryContactVisible = primaryContacts.some((contact) =>
    isContactFilled(contact),
  );

  const mbpClientSuccessPartnerDisplay = getMyBasePayTeamDisplayValue(
    myBasePayTeamResolved,
    'mbpClientSuccessPartner',
  );
  const mspSalesRepresentativeDisplay = getMyBasePayTeamDisplayValue(
    myBasePayTeamResolved,
    'mspSalesRepresentative',
  );
  const mbpBillingRepresentativeDisplay = getMyBasePayTeamDisplayValue(
    myBasePayTeamResolved,
    'mbpBillingRepresentative',
  );
  const mbpCollectionsRepresentativeDisplay = getMyBasePayTeamDisplayValue(
    myBasePayTeamResolved,
    'mbpCollectionsRepresentative',
  );
  const mspWorkforceSpecialistDisplay = getMyBasePayTeamDisplayValue(
    myBasePayTeamResolved,
    'mspWorkforceSpecialist',
  );

  const isMyBasePayTeamVisible =
    mbpClientSuccessPartnerDisplay.length > 0 ||
    mspSalesRepresentativeDisplay.length > 0 ||
    (isAdminRole &&
      (mbpBillingRepresentativeDisplay.length > 0 ||
        mbpCollectionsRepresentativeDisplay.length > 0 ||
        mspWorkforceSpecialistDisplay.length > 0));

  const isGeneralInfoFieldsVisible = !!(
    customerId ||
    recruiterCompanyName ||
    mainAddress ||
    customerGroupName ||
    customerInfo.companyWebSite ||
    customerInfo.additionalInvoiceName ||
    customerInfo.companyEmail ||
    customerInfo.companyPhoneNumber
  );

  const isLeftColumnVisible =
    isGeneralInfoFieldsVisible ||
    isPrimaryContactVisible ||
    isMyBasePayTeamVisible;

  const isBillingInfoVisible =
    billingContacts.length ||
    (billingInfo.location &&
      Object.values(billingInfo?.location).some(Boolean));

  const billingAddress = getLocation(
    billingInfo?.location?.countryName,
    billingInfo?.location?.address,
    billingInfo?.location?.state ||
      billingInfo?.location?.internationalStateName,
    billingInfo?.location?.city || billingInfo?.location?.internationalCityName,
    billingInfo?.location?.zip,
  );

  return (
    <Row gutter={[10, 10]}>
      {isLeftColumnVisible && (
        <Col xs={24} md={12}>
          <Row gutter={[10, 10]}>
            {isGeneralInfoFieldsVisible && (
              <Col xs={24} className="d-flex">
                <BasePageCard
                  className="stretch-card"
                  title="General information"
                  icons={<span className="icon icon-writing-tool-user"></span>}
                >
                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      {customerKey && (
                        <Col xs={24} md={12} data-id="customerId">
                          <div className="title">Customer ID:</div>
                          <div>{customerKey}</div>
                        </Col>
                      )}

                      {recruiterCompanyName && (
                        <Col xs={24} md={12} data-id="customerMemberCompany">
                          <div className="title">Member company:</div>

                          {isAdminRole ? (
                            <Link to={`/member/${recruiterCompanyKey}`}>
                              {recruiterCompanyName}
                            </Link>
                          ) : (
                            <div>{recruiterCompanyName}</div>
                          )}
                        </Col>
                      )}

                      {customerInfo.additionalInvoiceName && (
                        <Col xs={24} md={12}>
                          <div className="title">Invoice name (Legal/DBA):</div>
                          <div>{customerInfo.additionalInvoiceName}</div>
                        </Col>
                      )}

                      {mainAddress && (
                        <Col xs={24} md={12} data-id="customerAddress">
                          <div className="title">Head-office address:</div>
                          <div>{mainAddress}</div>
                        </Col>
                      )}

                      {customerInfo.companyWebSite && (
                        <Col xs={24} md={12} data-id="customerWebsite">
                          <div className="title">Company website:</div>
                          <div>
                            <a
                              href={getWebsiteLink(customerInfo.companyWebSite)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {getWebsiteName(customerInfo.companyWebSite)}
                            </a>
                          </div>
                        </Col>
                      )}

                      {customerInfo.companyEmail && (
                        <Col xs={24} md={12} data-id="customerAddress">
                          <div className="title">Email:</div>
                          <div>{customerInfo.companyEmail}</div>
                        </Col>
                      )}

                      {customerInfo.companyPhoneNumber && (
                        <Col
                          xs={24}
                          md={12}
                          data-id="customerCompanyPhoneNumber"
                        >
                          <div className="title">Phone number:</div>
                          <div>{customerInfo.companyPhoneNumber}</div>
                        </Col>
                      )}

                      {customerGroupName && (
                        <Col xs={24} md={12} data-id="customerGroup">
                          <div className="title">Customer Group:</div>
                          <div>{customerGroupName}</div>
                        </Col>
                      )}
                    </Row>
                  </div>
                </BasePageCard>
              </Col>
            )}

            {isPrimaryContactVisible && (
              <Col xs={24} className="d-flex">
                <BasePageCard
                  className="stretch-card"
                  title="Primary contact"
                  icons={<span className="icon icon-contacts"></span>}
                >
                  <div className="scroll-box scroll-box-lg">
                    {primaryContacts.map(
                      (contact, index) =>
                        isContactFilled(contact) && (
                          <PrimaryContactInfoSection
                            index={index}
                            contact={contact}
                            key={`client-primary-contact-${index}`}
                          />
                        ),
                    )}
                  </div>
                </BasePageCard>
              </Col>
            )}

            {isMyBasePayTeamVisible && (
              <Col xs={24} className="d-flex">
                <BasePageCard
                  className="stretch-card"
                  title="myBasePay Team"
                  icons={<span className="icon icon-user-settings"></span>}
                >
                  <div className="content-wrapper">
                    <Row gutter={[4, 4]} className="titled-sheet">
                      {isAdminRole &&
                        mbpBillingRepresentativeDisplay.length > 0 && (
                          <Col xs={24} md={12}>
                            <div className="title">
                              MBP Billing Representative:
                            </div>
                            <div>{mbpBillingRepresentativeDisplay}</div>
                          </Col>
                        )}

                      {mbpClientSuccessPartnerDisplay.length > 0 && (
                        <Col xs={24} md={12}>
                          <div className="title">MBP Client Success Partner:</div>
                          <div>{mbpClientSuccessPartnerDisplay}</div>
                        </Col>
                      )}

                      {isAdminRole &&
                        mbpCollectionsRepresentativeDisplay.length > 0 && (
                          <Col xs={24} md={12}>
                            <div className="title">
                              MBP Collections Representative:
                            </div>
                            <div>{mbpCollectionsRepresentativeDisplay}</div>
                          </Col>
                        )}

                      {mspSalesRepresentativeDisplay.length > 0 && (
                        <Col xs={24} md={12}>
                          <div className="title">MSP Sales Representative:</div>
                          <div>{mspSalesRepresentativeDisplay}</div>
                        </Col>
                      )}

                      {isAdminRole &&
                        mspWorkforceSpecialistDisplay.length > 0 && (
                          <Col xs={24} md={12}>
                            <div className="title">
                              MSP Workforce Specialist:
                            </div>
                            <div>{mspWorkforceSpecialistDisplay}</div>
                          </Col>
                        )}
                    </Row>
                  </div>
                </BasePageCard>
              </Col>
            )}
          </Row>
        </Col>
      )}

      <Col xs={24} md={12} className="d-flex">
        <Row gutter={[10, 10]}>
          {isBillingInfoVisible && (
            <Col xs={24} className="d-flex">
              <BasePageCard
                className="stretch-card"
                title="Billing information"
                icons={<span className="icon icon-pay"></span>}
              >
                <div className="content-wrapper">
                  <Row gutter={[4, 4]} className="titled-sheet">
                    {billingContacts.map((billingInfo, index) => (
                      <React.Fragment key={index}>
                        {(billingInfo?.firstName || billingInfo.lastName) && (
                          <Col xs={24} md={12} data-id="customerAp">
                            <div className="title">A/P: </div>
                            <div>
                              {getFullName(
                                billingInfo.firstName,
                                billingInfo.lastName,
                              )}
                            </div>
                          </Col>
                        )}

                        {billingInfo?.email && (
                          <Col xs={24} md={12} data-id="customerApEmail">
                            <div className="title">A/P email: </div>
                            <div>{billingInfo.email}</div>
                          </Col>
                        )}

                        {billingInfo?.phoneNumber && (
                          <Col xs={24} md={12} data-id="customerApPhoneNumber">
                            <div className="title">A/P phone number: </div>
                            <div>{billingInfo?.phoneNumber}</div>
                          </Col>
                        )}
                      </React.Fragment>
                    ))}
                    {billingAddress && (
                      <Col xs={24} md={12} data-id="customerBillingAddress">
                        <div className="title">Billing address: </div>
                        <div>{billingAddress}</div>
                      </Col>
                    )}
                  </Row>
                </div>
              </BasePageCard>
            </Col>
          )}

          <Col xs={24} className="d-flex">
            <BasePageCard
              className="stretch-card"
              title="Additional information"
              icons={<span className="icon icon-writing-tool"></span>}
            >
              <div className="content-wrapper">
                <Row gutter={[4, 4]} className="titled-sheet">
                  <Col xs={24} md={12} data-id="customerWorkWithCustomerBefore">
                    <div className="title">Worked with customer before: </div>
                    <div>
                      {additionalInfoResolved?.haveBusinessBefore ? 'Yes' : 'No'}
                    </div>
                  </Col>

                  {!isNull(additionalInfoResolved?.industryName) && (
                    <Col xs={24} md={12} data-id="customerIndustry">
                      <div className="title">Industry: </div>
                      <div>{additionalInfoResolved?.industryName}</div>
                    </Col>
                  )}

                  {!isNull(additionalInfoResolved?.yearsInBusiness) && (
                    <Col xs={24} md={12} data-id="customerYearsInBusiness">
                      <div className="title">Years in business: </div>
                      <div>
                        {getYearsInBusinessLabel(
                          additionalInfoResolved?.yearsInBusiness,
                        )}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfoResolved?.abilityToPay) && (
                    <Col xs={24} md={12} data-id="customerAbilityToPay">
                      <div className="title">Ability to pay: </div>
                      <div>
                        {getAbilityToPayLabel(additionalInfoResolved?.abilityToPay)}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfoResolved?.averageBillRate) && (
                    <Col xs={24} md={12} data-id="customerAverageBillRate">
                      <div className="title">Average bill rate: </div>
                      <div>
                        {formatNumberAsCurrency({
                          num: additionalInfoResolved?.averageBillRate,
                        })}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfoResolved?.potentialHeadcount) && (
                    <Col xs={24} md={12} data-id="customerPotentialHeadcount">
                      <div className="title">Potential headcount: </div>
                      <div>
                        {getClientSizeLabel(additionalInfoResolved?.potentialHeadcount)}
                      </div>
                    </Col>
                  )}

                  {!isNull(additionalInfoResolved?.estimatedValue) && (
                    <Col xs={24} md={12} data-id="customerProjectLength">
                      <div className="title">
                        Estimated length of the project:{' '}
                      </div>
                      <div>
                        {getProjectEstimation(
                          additionalInfoResolved?.estimatedValue,
                          additionalInfoResolved?.estimatedType,
                        )}
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </BasePageCard>
          </Col>
        </Row>
      </Col>
    </Row>
  );
}
